// features/migration/domainMigrationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate } from '../hooks/axiosInstance';

// Start domain migration
export const startDomainMigration = createAsyncThunk(
  'domainMigration/startMigration',
  async (migrationData, thunkAPI) => {
    try {
      const response = await axiosPrivate.post('/domain-migration/start', migrationData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Preview migration
export const previewMigration = createAsyncThunk(
  'domainMigration/previewMigration',
  async (migrationData, thunkAPI) => {
    try {
      const response = await axiosPrivate.post('/domain-migration/preview', migrationData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Get all migrations
export const getAllMigrations = createAsyncThunk(
  'domainMigration/getAllMigrations',
  async (params = {}, thunkAPI) => {
    try {
      const { page = 1, limit = 10 } = params;
      const response = await axiosPrivate.get(`/domain-migration/list?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Get migration status
export const getMigrationStatus = createAsyncThunk(
  'domainMigration/getMigrationStatus',
  async (migrationId, thunkAPI) => {
    try {
      const response = await axiosPrivate.get(`/domain-migration/${migrationId}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Cancel migration
export const cancelMigration = createAsyncThunk(
  'domainMigration/cancelMigration',
  async (migrationId, thunkAPI) => {
    try {
      const response = await axiosPrivate.put(`/domain-migration/${migrationId}/cancel`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

const domainMigrationSlice = createSlice({
  name: 'domainMigration',
  initialState: {
    // Current migration data
    currentMigration: null,
    
    // All migrations list
    migrations: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalMigrations: 0,
      limit: 10
    },
    
    // Preview data
    preview: null,
    
    // Loading states
    loading: false,
    previewLoading: false,
    migrationsLoading: false,
    statusLoading: false,
    
    // UI states
    error: null,
    success: null,
    
    // Migration form state
    formData: {
      oldDomain: '',
      newDomain: '',
      options: {
        dryRun: false,
        backupBeforeMigration: true,
        validateUrls: false,
      }
    },
    
    // Active migration tracking
    activeMigrationId: null,
    pollingInterval: null,
  },
  reducers: {
    // Clear messages
    clearMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    
    // Update form data
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    
    // Reset form
    resetForm: (state) => {
      state.formData = {
        oldDomain: '',
        newDomain: '',
        options: {
          dryRun: false,
          backupBeforeMigration: true,
          validateUrls: false,
        }
      };
      state.preview = null;
    },
    
    // Set active migration for polling
    setActiveMigration: (state, action) => {
      state.activeMigrationId = action.payload;
    },
    
    // Clear active migration
    clearActiveMigration: (state) => {
      state.activeMigrationId = null;
    },
    
    // Update current migration status (for polling)
    updateMigrationStatus: (state, action) => {
      if (state.currentMigration && state.currentMigration._id === action.payload._id) {
        state.currentMigration = action.payload;
      }
      
      // Also update in migrations list
      const index = state.migrations.findIndex(m => m._id === action.payload._id);
      if (index !== -1) {
        state.migrations[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Start Migration
      .addCase(startDomainMigration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startDomainMigration.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.msg;
        state.currentMigration = action.payload.migration;
        state.activeMigrationId = action.payload.migrationId;
        
        // Add to migrations list
        state.migrations.unshift(action.payload.migration);
        state.pagination.totalMigrations += 1;
      })
      .addCase(startDomainMigration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to start migration';
      })

      // Preview Migration
      .addCase(previewMigration.pending, (state) => {
        state.previewLoading = true;
        state.error = null;
      })
      .addCase(previewMigration.fulfilled, (state, action) => {
        state.previewLoading = false;
        state.preview = action.payload.preview;
      })
      .addCase(previewMigration.rejected, (state, action) => {
        state.previewLoading = false;
        state.error = action.payload?.msg || 'Failed to generate preview';
      })

      // Get All Migrations
      .addCase(getAllMigrations.pending, (state) => {
        state.migrationsLoading = true;
        state.error = null;
      })
      .addCase(getAllMigrations.fulfilled, (state, action) => {
        state.migrationsLoading = false;
        state.migrations = action.payload.migrations;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllMigrations.rejected, (state, action) => {
        state.migrationsLoading = false;
        state.error = action.payload?.msg || 'Failed to fetch migrations';
      })

      // Get Migration Status
      .addCase(getMigrationStatus.pending, (state) => {
        state.statusLoading = true;
        state.error = null;
      })
      .addCase(getMigrationStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.currentMigration = action.payload;
        
        // Update in migrations list
        const index = state.migrations.findIndex(m => m._id === action.payload._id);
        if (index !== -1) {
          state.migrations[index] = action.payload;
        }
      })
      .addCase(getMigrationStatus.rejected, (state, action) => {
        state.statusLoading = false;
        state.error = action.payload?.msg || 'Failed to fetch migration status';
      })

      // Cancel Migration
      .addCase(cancelMigration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelMigration.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.msg;
        state.currentMigration = action.payload.migration;
        state.activeMigrationId = null;
        
        // Update in migrations list
        const index = state.migrations.findIndex(m => m._id === action.payload.migration._id);
        if (index !== -1) {
          state.migrations[index] = action.payload.migration;
        }
      })
      .addCase(cancelMigration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to cancel migration';
      });
  }
});

export const {
  clearMessages,
  updateFormData,
  resetForm,
  setActiveMigration,
  clearActiveMigration,
  updateMigrationStatus,
} = domainMigrationSlice.actions;

export default domainMigrationSlice.reducer;